import { computed, Injectable, signal } from '@angular/core';
import { SystemDeployReviewFieldKey } from '@/app/interfaces/system-deploy-review-fields.types';

interface FieldPreviousReview {
  reviewers: string[];
}

const REVIEWER_LABELS: Record<string, string> = {
  RT: 'Revisor Tecnico',
  RJ: 'Revisor Juridico',
  RDPD: 'Revisor de Proteccion de Datos',
  RF: 'Revisor Funcional',
  GA: 'Grupo de Apoyo',
  JS: 'Jefe de Servicio',
};

const backendSelectedRoleMap: Record<string, string> = {
  selectedSupportGroup: 'GA',
  selectedRevisorTecnico: 'RT',
  selectedRevisorJuridico: 'RJ',
  selectedRevisorFuncional: 'RF',
  selectedRevisorDpd: 'RDPD',
};

@Injectable({ providedIn: 'root' })
export class SystemDeployReviewFieldsService {
  private readonly reviewState = signal<Partial<Record<SystemDeployReviewFieldKey, boolean>>>({});
  readonly markedFields$ = computed<SystemDeployReviewFieldKey[]>(() =>
    Object.entries(this.reviewState())
      .filter(([, marked]) => marked)
      .map(([key]) => key as SystemDeployReviewFieldKey)
  );
  private readonly previousReviewState = signal<Partial<Record<SystemDeployReviewFieldKey, FieldPreviousReview>>>({});

  isFieldMarked(fieldKey: SystemDeployReviewFieldKey): boolean {
    return this.reviewState()[fieldKey] ?? false;
  }

  markField(fieldKey: SystemDeployReviewFieldKey, marked: boolean): void {
    this.reviewState.update((state) => ({
      ...state,
      [fieldKey]: marked,
    }));
  }

  toggleField(fieldKey: SystemDeployReviewFieldKey): void {
    this.markField(fieldKey, !this.isFieldMarked(fieldKey));
  }

  getMarkedFieldKeys(): SystemDeployReviewFieldKey[] {
    return this.markedFields$();
  }

  getMarkedFieldsForBackend(): string[] {
    return this.getMarkedFieldKeys();
  }

  restoreMarkedFields(markedFields: SystemDeployReviewFieldKey[]): void {
    const nextState: Partial<Record<SystemDeployReviewFieldKey, boolean>> = {};

    markedFields.forEach((fieldKey) => {
      nextState[fieldKey] = true;
    });

    this.reviewState.set(nextState);
  }

  reset(): void {
    this.reviewState.set({});
    this.previousReviewState.set({});
  }

  hasPreviousReview(fieldKey: SystemDeployReviewFieldKey): boolean {
    return !!this.previousReviewState()[fieldKey]?.reviewers?.length;
  }

  getPreviousReviewers(fieldKey: SystemDeployReviewFieldKey): string[] {
    return this.previousReviewState()[fieldKey]?.reviewers ?? [];
  }

  loadPreviousReviewsFromBackend(data: unknown): void {
    const nextPreviousState: Partial<Record<SystemDeployReviewFieldKey, FieldPreviousReview>> = {};
    const nextMarkedState: Partial<Record<SystemDeployReviewFieldKey, boolean>> = {};
    const reviewData = this.asReviewData(data);

    Object.entries(backendSelectedRoleMap).forEach(([backendKey, roleCode]) => {
      const selectedFields = reviewData[backendKey];

      if (!Array.isArray(selectedFields)) {
        return;
      }

      selectedFields.forEach((fieldName) => {
        const fieldKey = fieldName as SystemDeployReviewFieldKey;
        nextMarkedState[fieldKey] = true;
        const reviewers = nextPreviousState[fieldKey]?.reviewers ?? [];

        if (!reviewers.includes(roleCode)) {
          nextPreviousState[fieldKey] = {
            reviewers: [...reviewers, roleCode],
          };
        }
      });
    });

    this.previousReviewState.set(nextPreviousState);
    this.reviewState.set(nextMarkedState);
  }

  getReviewerLabels(codes: string[]): string[] {
    return codes.map((code) => REVIEWER_LABELS[code] ?? code);
  }

  getPreviousReviewTooltipText(fieldKey: SystemDeployReviewFieldKey): string {
    const reviewers = this.getPreviousReviewers(fieldKey);

    if (!reviewers.length) {
      return '';
    }

    return `Campo marcado por: ${this.getReviewerLabels(reviewers).join(', ')}`;
  }

  private asReviewData(data: unknown): Record<string, string[] | undefined> {
    return data && typeof data === 'object' ? (data as Record<string, string[] | undefined>) : {};
  }
}
